// context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { Store } from "../entities/interfaces/stores";
import StoreService from "../services/stores.service";
import { useAuth } from "./AuthContext";

type StoreContextType = {
  selectedStore: Store | undefined;
  stores: Store[];
  changeStore: (id?: string) => void;
  fetchStores: () => Promise<void>;
};

const StoreContext = createContext<StoreContextType | null>(null);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store>();

  useEffect(() => {
    if (isAuthenticated) {
      fetchStores();
    }
  }, [isAuthenticated]);

  const fetchStores = async () => {
    const res = await StoreService.list();
    if (res?.success) {
      setStores(res.data);
      if (res.data.length) {
        const previouslySelectedStore = localStorage.getItem("storeId");
        if (previouslySelectedStore) {
          const previousStore = res.data.find(
            (store) => store.id === previouslySelectedStore
          );
          setSelectedStore(previousStore);
          setStoreIdInLocalStorage(previousStore?.id);
        } else {
          setSelectedStore(res.data[0]);
          setStoreIdInLocalStorage(res.data[0].id);
        }
      }
    }
  };

  const changeStore = (id?: string) => {
    if (!id) {
      setSelectedStore(undefined);
      setStoreIdInLocalStorage(undefined);
      return;
    }
    const foundStore = stores.find((s) => s.id === id);
    setSelectedStore(foundStore);
    setStoreIdInLocalStorage(foundStore?.id);
  };

  const setStoreIdInLocalStorage = (storeId?: string) => {
    localStorage.setItem("storeId", storeId || "");
  };

  return (
    <StoreContext.Provider
      value={{ stores, selectedStore, changeStore, fetchStores }}
      key={selectedStore?.id}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
